/**
 * Bootstrap component for force update functionality.
 *
 * This component:
 * 1. Fetches the initial force update timestamp on app load
 * 2. Sets up polling every 60 seconds to check for updates
 * 3. The axios interceptor handles checking on every API call
 */
import { useEffect, useRef } from "react";

import { apiAxios } from "@/api/config";
import { useForceUpdateStore } from "@/stores/forceUpdate";

const POLL_INTERVAL_MS = 60 * 1000; // 1 minute

interface ForceUpdateResponse {
  timestamp: number | null;
}

export function ForceUpdateBootstrap() {
  const { initialized, setInitialTimestamp, checkForUpdate } = useForceUpdateStore();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch initial timestamp on mount
  useEffect(() => {
    if (initialized) {
      return;
    }

    const fetchInitialTimestamp = async () => {
      try {
        const response = await apiAxios.get<ForceUpdateResponse>("/force-update");
        setInitialTimestamp(response.data.timestamp);
      } catch (error) {
        // If we can't fetch the timestamp, initialize with null
        // This prevents blocking the app if the endpoint is unavailable
        console.debug("[ForceUpdate] Failed to fetch initial timestamp:", error);
        setInitialTimestamp(null);
      }
    };

    void fetchInitialTimestamp();
  }, [initialized, setInitialTimestamp]);

  // Set up polling
  useEffect(() => {
    if (!initialized) {
      return;
    }

    const poll = async () => {
      try {
        const response = await apiAxios.get<ForceUpdateResponse>("/force-update");
        checkForUpdate(response.data.timestamp);
      } catch (error) {
        // Silent failure - don't disrupt the user
        console.debug("[ForceUpdate] Polling failed:", error);
      }
    };

    pollingRef.current = setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [initialized, checkForUpdate]);

  return null;
}
